import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const MagicShowNearMePage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const faqs = [
    {
      question: "Where is the magic show located?",
      answer: "Mental Hop Scotch is performed in Orlando, FL. We're conveniently located with easy access from I-4 and major roads throughout Central Florida."
    },
    {
      question: "What cities is this magic show near?",
      answer: "Our Orlando location is easily accessible from Kissimmee, Winter Park, Altamonte Springs, Sanford, Lake Mary, Celebration, Dr. Phillips, and all Central Florida communities. Most locations are within 30-45 minutes."
    },
    {
      question: "Is there parking available?",
      answer: "Yes! Convenient on-site parking is available for all guests. We recommend arriving 30 minutes early to park and get settled before the show."
    },
    {
      question: "What kind of magic show is this?",
      answer: "Mental Hop Scotch is a 90-minute mental magic and mentalism experience. Unlike traditional magic with big props, Simon Nomis performs mind reading, predictions, and psychological illusions where the audience participates directly."
    },
    {
      question: "When are the show times?",
      answer: "Shows are every Saturday at 7:00 PM. The show runs approximately 90 minutes with a 15-minute intermission."
    },
    {
      question: "How much do tickets cost?",
      answer: "Tickets range from $35 (Standard) to $70 (VIP). All ticket levels include the full 90-minute show experience. VIP adds meet & greet, front row seating, and exclusive merchandise."
    },
    {
      question: "Is this good for tourists?",
      answer: "Absolutely! If you're visiting Orlando and looking for entertainment beyond the theme parks, Mental Hop Scotch offers a unique, intimate experience you won't find anywhere else in Central Florida."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Simon Nomis Live - Mental Hop Scotch",
    "description": "Magic show near you in Orlando, FL. Mental Hop Scotch is an interactive mentalism experience.",
    "url": "https://www.simonnomislive.com/magic-show-near-me",
    "image": "https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg",
    "telephone": "+1-407-442-1933",
    "email": "simon@simonnomismagic.com",
    "address": { "@type": "PostalAddress", "addressLocality": "Orlando", "addressRegion": "FL", "addressCountry": "US" },
    "geo": { "@type": "GeoCoordinates", "latitude": "28.4567", "longitude": "-81.4011" },
    "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "19:00", "closes": "21:00" },
    "priceRange": "$35-$70"
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Magic Show Near Me | Orlando FL | Mental Hop Scotch | Simon Nomis</title>
        <meta name="description" content="Looking for a magic show near you? Mental Hop Scotch in Orlando, FL is Central Florida's premier mentalism experience. Saturdays at 7PM." />
        <meta name="keywords" content="magic show near me, magic show Orlando, magic show Central Florida, magic show Kissimmee, entertainment near me, live show near me, mentalist near me" />
        <link rel="canonical" href="https://www.simonnomislive.com/magic-show-near-me" />
        <meta property="og:title" content="Magic Show Near Me | Orlando FL" />
        <meta property="og:description" content="Looking for a magic show near you? Mental Hop Scotch in Orlando, FL is Central Florida's premier mentalism experience!" />
        <meta property="og:url" content="https://www.simonnomislive.com/magic-show-near-me" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-black via-purple-900/30 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6"><span className="text-purple-400">Magic Show</span> Near Me</h1>
            <p className="text-xl md:text-2xl text-purple-300 mb-8">Orlando's Premier Live Magic Experience</p>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              Searching for a <strong className="text-purple-400">magic show near you</strong>? Mental Hop Scotch in Orlando is Central Florida's most talked-about entertainment experience. Just minutes from Kissimmee, Winter Park, and surrounding areas.
            </p>
            <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Get Tickets Now</a>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Our</span> Location</h2>
            <div className="bg-gray-900/50 p-8 rounded-lg border border-purple-500/30 text-center">
              <h3 className="text-2xl font-bold text-purple-400 mb-4">🏛️ Orlando, FL</h3>
              <p className="text-xl text-white mb-2">Visit simonnomislive.com for venue details</p>
              <p className="text-xl text-white mb-6">Orlando, FL 32809</p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-black/30 p-4 rounded-lg">
                  <p className="text-purple-400 font-bold">📅 Show Days</p>
                  <p className="text-white">Every Saturday</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg">
                  <p className="text-purple-400 font-bold">⏰ Show Time</p>
                  <p className="text-white">7:00 PM</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg">
                  <p className="text-purple-400 font-bold">🅿️ Parking</p>
                  <p className="text-white">Free On-Site</p>
                </div>
              </div>
              <a href="https://www.google.com/maps/search/?api=1&query=1300+La+Quinta+Dr+Orlando+FL+32809" target="_blank" rel="noreferrer" className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105">📍 Get Directions</a>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Serving <span className="text-purple-400">Central Florida</span></h2>
            <p className="text-gray-300 text-center mb-8">Easy to reach from all these nearby areas:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['Orlando', 'Kissimmee', 'Winter Park', 'Altamonte Springs', 'Sanford', 'Lake Mary', 'Celebration', 'Dr. Phillips', 'Windermere', 'Ocoee', 'Winter Garden', 'Clermont'].map((city) => (
                <div key={city} className="bg-purple-900/20 p-3 rounded-lg border border-purple-500/30 text-center">
                  <span className="text-white">{city}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Show */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <img src="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" alt="Magic Show Near Me Orlando" className="rounded-lg shadow-2xl" />
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">About <span className="text-purple-400">The Show</span></h2>
                <p className="text-gray-300 mb-4">Mental Hop Scotch is a 90-minute mental magic experience where audience members become the stars. Master mentalist Simon Nomis reads minds, makes impossible predictions, and creates moments of pure wonder.</p>
                <p className="text-gray-300 mb-6">Unlike traditional magic shows, this intimate experience puts you at the center of the action. You won't just watch magic—you'll be part of it.</p>
                <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-lg text-white rounded-full px-8 py-3 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Book Your Tickets →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Frequently Asked</span> Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg border border-purple-500/30 overflow-hidden">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-900/20 transition-colors">
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <span className="text-purple-400 text-2xl flex-shrink-0">{openFaq === index ? '−' : '+'}</span>
                  </button>
                  {openFaq === index && <div className="px-6 pb-4"><p className="text-gray-300 leading-relaxed">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Your <span className="text-purple-400">Local Magic Show</span> Awaits</h2>
          <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-12 py-5 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Buy Tickets Now</a>
          <p className="text-gray-400 mt-6 text-sm">📍 Orlando, FL | Saturdays at 7PM</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MagicShowNearMePage;